import { ACTING_ROLE_LABELS, ACTING_ROLE_SHORT_LABELS, offerableRoles, type ActingRole } from '@brush/auth';
import type { RoleChoice } from '@brush/ui';

/**
 * The portal is the one app all three roles mean something in, and they mean three different
 * things here: staff browse somebody else's portal read-only and are scoped by the server to the
 * rooms they may see, while a player reads their own.
 *
 * Player is last rather than first, matching the privilege order the server reports and the
 * default the API falls back to.
 */
export const SUPPORTED_ROLES: readonly ActingRole[] = ['admin', 'room', 'player'];

/**
 * What each role does *in the portal* — which is not what it does in the room console, so these
 * sentences are the app's own rather than shared. The distinction that matters here is whose
 * portal you end up looking at and how much of the platform you can reach to find it.
 */
const DESCRIPTIONS: Record<ActingRole, string> = {
  admin: 'Look up any player, at any room. Read-only.',
  room: 'Look up players at the rooms you operate. Read-only.',
  player: 'Your own results, leaderboards and ratings.',
};

export function roleChoicesFor(availableRoles: readonly string[]): RoleChoice[] {
  return offerableRoles(availableRoles, SUPPORTED_ROLES).map((role) => ({
    id: role,
    label: ACTING_ROLE_LABELS[role],
    description: DESCRIPTIONS[role],
  }));
}

export function shortLabelFor(role: ActingRole): string {
  return ACTING_ROLE_SHORT_LABELS[role];
}

/**
 * Shown when a stored choice is no longer accepted — the server answered
 * `acting_role_not_held`, which happens when a room assignment is withdrawn between sessions.
 * Without saying so, the chooser reappearing reads as the app having forgotten who you are.
 */
export const STALE_ROLE_NOTICE =
  'The role you were using is no longer available to your account. Choose another to carry on.';

/**
 * What a staff account acting as a player is told, on the cards screen, when it holds no card.
 *
 * Acting as a player is honest narrowing: the server drops both staff claims, so the player search
 * returns nobody. What has changed is that this is no longer a dead end — a sign-in with no card
 * can connect one through the flow everybody uses (ADR 0010), and that screen is what carries this
 * line. So it names the two ways on, in the order they are likely to be wanted: the role they set
 * aside is usually what a member of staff meant, and the claim is there if it is not.
 *
 * It is not an error message and must not read as one. `RoleSwitch` renders it only where there is
 * another role to go back to, so a player with no card never sees it at all.
 */
export const STAFF_AS_PLAYER_NOTICE =
  "You're acting as a player, so you're looking at your own cards rather than the rooms you work at. Choose another role to look a player up — or connect a card of your own below.";
